import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuthStatus } from "../components/hooks/useAuthStatus";
import AppMenu from "../components/layout/AppMenu";
import Spinner from "../components/layout/Spinner";
import {
    refreshAdminEmails,
    refreshBetaTesterEmails,
    setAdminEmails,
    setBetaTesterEmails,
} from "../components/utils/access";
import { useLanguage } from "../context/Language/LanguageContext";

const kEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// A small add/remove email-list editor shared by the beta-tester and admin
// sections below.
function EmailListEditor({ emails, onAdd, onRemove, saving, t, emptyLabel }) {
  const [newEmail, setNewEmail] = useState("");

  const add = () => {
    const email = newEmail.trim().toLowerCase();
    if (!kEmailRegex.test(email)) {
      toast.error(t("Enter a valid email address."));
      return;
    }
    if (emails.includes(email)) {
      toast.info(t("That email is already in the list."));
      return;
    }
    setNewEmail("");
    onAdd(email);
  };

  return (
    <>
      <div className="flex gap-2 mt-2">
        <input
          type="email"
          className="input-generic flex-1"
          placeholder={t("name@example.com")}
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") add();
          }}
          disabled={saving}
        />
        <button
          className="btn btn-sm btn-primary"
          onClick={add}
          disabled={saving || !newEmail.trim()}
        >
          {t("Add")}
        </button>
      </div>

      <ul className="mt-3 divide-y divide-base-300 rounded border border-base-300">
        {emails.length === 0 && (
          <li className="p-3 text-sm text-base-content/60">{emptyLabel}</li>
        )}
        {emails.map((email) => (
          <li
            key={email}
            className="flex items-center justify-between p-2 px-3"
          >
            <span className="text-sm">{email}</span>
            <button
              className="btn btn-xs btn-ghost text-error"
              onClick={() => onRemove(email)}
              disabled={saving}
            >
              {t("Remove")}
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

function Admin() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { firebaseUser, checkingStatus } = useAuthStatus();
  const [loading, setLoading] = useState(true);
  const [allowed, setAllowed] = useState(false);
  const [testers, setTesters] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [savingTesters, setSavingTesters] = useState(false);
  const [savingAdmins, setSavingAdmins] = useState(false);

  const myEmail = firebaseUser?.email?.toLowerCase() ?? "";

  // Gate on the FRESH Firestore admin list (not the sync cache isAdminUser()
  // relies on elsewhere) so a newly-promoted admin isn't locked out the first
  // time they open this page, before the cache has refreshed in the background.
  useEffect(() => {
    if (!firebaseUser) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    refreshAdminEmails().then((adminEmails) => {
      if (cancelled) return;
      const isAdmin = adminEmails.includes(myEmail);
      setAllowed(isAdmin);
      setAdmins(adminEmails);
      if (!isAdmin) {
        setLoading(false);
        return;
      }
      refreshBetaTesterEmails().then((testerEmails) => {
        if (!cancelled) {
          setTesters(testerEmails);
          setLoading(false);
        }
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser]);

  const persistTesters = async (next) => {
    setSavingTesters(true);
    try {
      setTesters(await setBetaTesterEmails(next));
    } catch (e) {
      console.log("Failed to save beta tester list", e);
      toast.error(t("Failed to save changes."));
    } finally {
      setSavingTesters(false);
    }
  };

  const persistAdmins = async (next) => {
    setSavingAdmins(true);
    try {
      setAdmins(await setAdminEmails(next));
    } catch (e) {
      console.log("Failed to save admin list", e);
      toast.error(
        e?.message === "At least one admin is required."
          ? t("At least one admin is required.")
          : t("Failed to save changes.")
      );
    } finally {
      setSavingAdmins(false);
    }
  };

  const removeAdmin = (email) => {
    if (
      email === myEmail &&
      !window.confirm(
        t("Remove yourself as admin? You will immediately lose access to this page.")
      )
    ) {
      return;
    }
    persistAdmins(admins.filter((e) => e !== email));
  };

  if (checkingStatus || loading) {
    return <Spinner />;
  }

  if (!firebaseUser || !allowed) {
    return (
      <div className="flex flex-col items-center gap-3 py-10">
        <AppMenu current="/admin" />
        <p className="text-lg font-semibold">{t("Access denied")}</p>
        <p className="text-sm text-base-content/60">
          {t("You don't have permission to view this page.")}
        </p>
        <button className="btn btn-sm btn-ghost" onClick={() => navigate(-1)}>
          {t("Back")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex justify-center py-6">
      <div className="card w-full max-w-2xl bg-base-100 shadow-xl">
        <div className="card-body">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AppMenu current="/admin" />
              <h2 className="card-title">{t("Admin")}</h2>
            </div>
            <button className="btn btn-sm btn-ghost" onClick={() => navigate(-1)}>
              {t("Back")}
            </button>
          </div>

          <div className="divider my-2" />

          <h3 className="font-semibold text-lg">{t("Beta testers")}</h3>
          <p className="text-sm text-base-content/70">
            {t(
              "Emails in this list get early access to AI Reports and other gated features. Changes take effect immediately."
            )}
          </p>

          <EmailListEditor
            emails={testers}
            onAdd={(email) => persistTesters([...testers, email])}
            onRemove={(email) => persistTesters(testers.filter((e) => e !== email))}
            saving={savingTesters}
            t={t}
            emptyLabel={t("No beta testers yet.")}
          />

          <div className="divider my-2" />

          <h3 className="font-semibold text-lg">{t("Admins")}</h3>
          <p className="text-sm text-base-content/70">
            {t("Emails in this list can manage beta testers and other admins.")}
          </p>
          <EmailListEditor
            emails={admins}
            onAdd={(email) => persistAdmins([...admins, email])}
            onRemove={removeAdmin}
            saving={savingAdmins}
            t={t}
            emptyLabel={t("No admins yet.")}
          />

          <div className="divider my-2" />
          <p className="text-xs text-base-content/50">
            {t(
              "For stronger security later, consider moving admin checks to a Firebase Auth custom claim instead of this email list."
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Admin;
